<template>
  <vxe-table
    v-if="!isMobile"
    class="history-table"
    empty-text="No data"
    border="none"
    :data="historyDataList"
    :cell-class-name="cellClassName"
  >
    <vxe-column
      field="date"
      title="Date"
      width="25%"
      sortable
      sort-by="date"
      :formatter="formatDate"
    ></vxe-column>
    <vxe-column
      field="trigger"
      title="Triggers"
      width="45%"
      sortable
      sort-by="trigger"
    ></vxe-column>
    <vxe-column
      field="incident"
      title="Incident"
      width="30%"
      sortable
      sort-by="incident"
      :formatter="formatIncident"
    ></vxe-column>
  </vxe-table>
  <div v-else class="mobile">
    <div v-if="!historyDataList.length" class="no-data">
      <p>No data</p>
    </div>
    <div v-else>
      <div class="icon-group-and-search">
        <div class="icon-group">
          <fa
            icon="sort-amount-down"
            class="icon"
            :class="{ active: isShowSortTool }"
            @click="toggleIsShowSortTool"
          ></fa>
        </div>
      </div>
      <div v-if="isShowSortTool" class="tool">
        <SortAndFilterTool
          :toolConfig="sortToolConfig"
          @onToolChange="handleSortToolChange"
        ></SortAndFilterTool>
      </div>
    </div>
    <ul>
      <li v-for="history in processedHistoryDataList" :key="history.id">
        <div class="date">
          <span>Date</span>
          <span>{{ history.date | dateFormatter }}</span>
        </div>
        <div class="trigger">
          <span>Triggers</span>
          <span>{{ history.trigger }}</span>
        </div>
        <div class="incident">
          <span>Incident</span>
          <span> {{ formatIncidentOnMobile(history.incident) }} </span>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { mapGetters } from 'vuex';
import { HISTORY_SORT_OPTION } from '../../../../constants/alertsConstant';
import SortAndFilterTool from '../alertTableParts/SortAndFilterTool.vue';

export default {
  name: 'HistoryTable',
  components: { SortAndFilterTool },
  props: ['historyDataList'],
  data() {
    return {
      isShowSortTool: false,
      sortType: 0,
      sortToolConfig: {
        placeholder: 'Sort',
        toolList: [
          { id: HISTORY_SORT_OPTION.NONE, text: 'None' },
          {
            id: HISTORY_SORT_OPTION.NEWEST_FIRST,
            text: 'Newest first'
          },
          {
            id: HISTORY_SORT_OPTION.OLDEST_FIRST,
            text: 'Oldest first'
          },
          {
            id: HISTORY_SORT_OPTION.TRIGGER_LOWEST_FIRST,
            text: 'Trigger: lowest first'
          },
          {
            id: HISTORY_SORT_OPTION.TRIGGER_HIGHEST_FIRST,
            text: 'Trigger: highest first'
          }
        ]
      }
    };
  },
  computed: {
    ...mapGetters(['isMobile']),
    processedHistoryDataList() {
      // Deep clone list as sort function below will change the origin list.
      let list = JSON.parse(JSON.stringify(this.historyDataList));

      switch (this.sortType) {
        case HISTORY_SORT_OPTION.NONE:
          break;
        case HISTORY_SORT_OPTION.NEWEST_FIRST:
          list.sort((a, b) => dayjs(b.date) - dayjs(a.date));
          break;
        case HISTORY_SORT_OPTION.OLDEST_FIRST:
          list.sort((a, b) => dayjs(a.date) - dayjs(b.date));
          break;
        case HISTORY_SORT_OPTION.TRIGGER_HIGHEST_FIRST:
          list.sort((a, b) => {
            const triggerA = (a.trigger || '').toString();
            const triggerB = (b.trigger || '').toString();
            if (triggerB < triggerA) return -1;
            if (triggerB > triggerA) return 1;

            return 0;
          });
          break;
        case HISTORY_SORT_OPTION.TRIGGER_LOWEST_FIRST:
          list.sort((a, b) => {
            const triggerA = (a.trigger || '').toString();
            const triggerB = (b.trigger || '').toString();
            if (triggerB < triggerA) return 1;
            if (triggerB > triggerA) return -1;

            return 0;
          });
          break;
        default:
          break;
      }

      return list;
    }
  },
  methods: {
    // Add class name "word-break--normal" to cells in Triggers and Incident column to avoid break word.
    cellClassName({ column }) {
      if (column.property === 'trigger' || column.property === 'incident') {
        return 'word-break--normal';
      }
    },
    formatDate({ cellValue }) {
      const date = dayjs(cellValue).toString()
        ? dayjs(cellValue)
            .toString()
            .substring(4, 16)
        : '';
      return date;
    },
    formatIncident({ cellValue }) {
      if (cellValue.indexOf('First hit') === 0) return 'First hit';

      return cellValue;
    },
    formatIncidentOnMobile(val) {
      if (val.indexOf('First hit') === 0) return 'First hit';

      return val;
    },
    toggleIsShowSortTool() {
      this.isShowSortTool = !this.isShowSortTool;
      if (!this.isShowSortTool) this.sortType = HISTORY_SORT_OPTION.NONE;
    },
    handleSortToolChange(val) {
      this.sortType = val;
    }
  }
};
</script>

<style lang="scss" scoped>
.history-table {
  /deep/ .vxe-header--row {
    background-color: #ffffff !important;
  }

  /deep/ td.vxe-body--column {
    background-image: none;
  }
}

.mobile {
  .no-data {
    padding: 1rem 0;
  }

  ul {
    list-style: none;
  }

  li {
    border-bottom: 1px solid #ddd;
    padding-top: 1rem;
  }

  .date,
  .incident,
  .trigger {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    margin-bottom: 1rem;
  }

  .date span:nth-child(1),
  .incident span:nth-child(1),
  .trigger span:nth-child(1) {
    color: $icon-grey;
    font-weight: 600;
    padding-right: 1rem;
  }

  .date span:nth-child(2),
  .incident span:nth-child(2),
  .trigger span:nth-child(2) {
    text-align: right;
  }

  .icon-group-and-search {
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;

    .icon {
      padding: 0.5rem;
      margin-right: 0.5rem;
      width: 2rem;
      height: 2rem;
      color: $icon-grey;
    }

    .active {
      color: $text-light-blue;
    }
  }

  .tool {
    padding-bottom: 0.5rem;
  }
}
</style>
